/**
 * Web Serial Print Service - Direct ESC/POS printing via Serial Port
 * Works with most USB thermal printers (they expose a virtual COM port).
 * More compatible than WebUSB for printers like Epson, Star, Bixolon, etc.
 * Requires Chrome 89+ or Edge 89+.
 */

import { buildProfessionalReceiptBytes } from "./receiptBuilder";

let serialPort = null;
let writer = null;

export function isWebSerialSupported() {
  return "serial" in navigator;
}

export function isSerialPrinterConnected() {
  return serialPort !== null && serialPort.readable !== null;
}

/**
 * Request user to select a serial printer (shows browser's native port picker)
 * Must be called from a user gesture (click)
 */
export async function requestSerialPrinter() {
  if (!navigator.serial) {
    throw new Error("Web Serial nao suportado. Use Chrome ou Edge.");
  }
  try {
    const port = await navigator.serial.requestPort();
    await openSerialPort(port);
    localStorage.setItem("serial_printer_connected", "true");
    return { name: "Impressora Serial/USB", connected: true };
  } catch (err) {
    if (err.name === "NotFoundError") {
      throw new Error("Nenhuma porta selecionada.");
    }
    throw err;
  }
}

async function openSerialPort(port) {
  // Common baud rates for thermal printers: 9600, 19200, 38400, 57600, 115200
  // Try 9600 first (most common default), can be configured via settings
  const baudRate = parseInt(localStorage.getItem("serial_baud_rate") || "9600", 10);
  await port.open({ baudRate });
  serialPort = port;
  writer = port.writable.getWriter();
}

/**
 * Try to reconnect to previously paired printer
 */
export async function reconnectSerialPrinter() {
  if (!navigator.serial) return null;
  try {
    const ports = await navigator.serial.getPorts();
    if (ports.length > 0) {
      for (const port of ports) {
        try {
          // Release existing writer if any
          if (writer) {
            try { writer.releaseLock(); } catch {}
            writer = null;
          }
          if (!port.readable) {
            await openSerialPort(port);
          } else {
            serialPort = port;
            if (port.writable) {
              writer = port.writable.getWriter();
            }
          }
          return { name: "Impressora Serial/USB", connected: true };
        } catch {
          continue;
        }
      }
    }
  } catch {}
  return null;
}

export async function disconnectSerialPrinter() {
  try {
    if (writer) {
      await writer.close();
      writer = null;
    }
    if (serialPort) {
      await serialPort.close();
      serialPort = null;
    }
  } catch {}
  localStorage.removeItem("serial_printer_connected");
}

// --- ESC/POS Commands ---
// Now using professional receipt builder from receiptBuilder.js

/**
 * Print order receipt via Web Serial - Professional layout
 */
export async function printViaSerial(order, options = {}) {
  if (!writer || !serialPort) {
    const reconnected = await reconnectSerialPrinter();
    if (!reconnected) throw new Error("Impressora serial nao conectada.");
  }

  // Build restaurant config from options
  const restaurant = {
    name: options.storeName || "Restaurante",
    address: options.address || "",
    phone: options.phone || "",
    cif: options.cif || "",
    currency: options.currency || "eur",
    menu_url: options.menuUrl || "",
    slug: options.slug || "",
    show_qr_code: options.showQRCode !== false,
    qr_message: options.qrMessage || "Escaneie e veja nosso menu",
    qr_size: options.qrSize || 5,
    receipt_footer: options.receiptFooter || "Obrigado pela preferencia!",
    instagram_url: options.instagram || "",
    whatsapp_phone: options.whatsappPhone || "",
  };

  const data = buildProfessionalReceiptBytes(order, restaurant, { columns: options.columns || 32 });
  const copies = options.copies || 1;

  try {
    for (let i = 0; i < copies; i++) {
      await writer.write(data);
      if (copies > 1 && i < copies - 1) {
        await new Promise(r => setTimeout(r, 500));
      }
    }
    return { success: true, method: "serial" };
  } catch (err) {
    try { writer.releaseLock(); } catch {}
    writer = null;
    const reconnected = await reconnectSerialPrinter();
    if (reconnected && writer) {
      await writer.write(data);
      return { success: true, method: "serial" };
    }
    throw new Error("Impressora serial perdeu conexao: " + err.message);
  }
}

export async function printTestPageSerial() {
  const testOrder = {
    id: "TEST-SER-123456",
    order_number: "TST001",
    customer_name: "Teste Impressao",
    customer_phone: "+351 912 345 678",
    source: "online",
    items: [
      { product_name: "Sushi Mix Especial", quantity: 2, unit_price: 15.90, notes: "Sem wasabi" },
      { product_name: "Temaki Salmao", quantity: 1, unit_price: 8.50 },
      { product_name: "Coca-Cola 330ml", quantity: 2, unit_price: 2.50 },
    ],
    subtotal: 45.30,
    total: 45.30,
    payment_method: "cartao",
    created_at: new Date().toISOString(),
  };
  return await printViaSerial(testOrder, {
    storeName: "KAISO SUSHI",
    address: "Rua Exemplo, 123",
    phone: "+351 912 345 678",
    receiptFooter: "Obrigado! Volte sempre!",
    menuUrl: "https://kaisosystem.com/menu/kaiso-sushi",
    showQRCode: true,
    qrMessage: "Escaneie e veja nosso menu",
  });
}
